/**
 * Public digital business card.
 *
 * What someone sees a few seconds after scanning a QR code, usually one-handed
 * on a phone, often on a conference floor with poor signal. The design follows
 * from that: the person's name and how to reach them come first and need no
 * interaction, the actions are thumb-sized, and the contact form is four
 * fields because a fifth is where people stop.
 *
 * Unauthenticated. Nothing sensitive is behind this route.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Linkedin, Globe, Mail, Phone, Download, Check, Loader2 } from 'lucide-react';

import config, { BRAND_ASSETS } from '../config';
import '../styles/business-card.css';

type Card = {
  id: string;
  slug: string;
  full_name: string;
  job_title?: string | null;
  organization?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  website_url?: string | null;
  headline?: string | null;
};

const cardApi = `${config.apiBaseUrl}/business-card`;

export default function BusinessCard() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();

  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The scan id ties a captured contact back to the view that produced it.
  const scanId = useRef<string | null>(null);
  // React 18 StrictMode mounts effects twice in development; without this the
  // scan counter double-counts every local page load.
  const scanRecorded = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${cardApi}/${slug}`);
        if (!res.ok) { if (!cancelled) setNotFound(true); return; }
        const body = await res.json();
        if (!cancelled) setCard(body.data);

        if (!scanRecorded.current) {
          scanRecorded.current = true;
          // Fire and forget. A failed scan count must never stop the card
          // rendering — the card is the point, the analytics are not.
          fetch(`${cardApi}/${slug}/scan`, { method: 'POST' })
            .then((r) => (r.ok ? r.json() : null))
            .then((b) => { scanId.current = b?.data?.scan_id ?? null; })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${cardApi}/${slug}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          scan_id: scanId.current || undefined,
          source: params.get('source') || 'business_card',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The platform error envelope puts the human-readable text in
        // `message` and a machine code such as INTERNAL_ERROR in `error`.
        // Reading `error` first would show that code to a stranger who
        // mistyped their email, so prefer `message` and never surface a code.
        const detail = typeof body?.message === 'string' ? body.message : null;
        throw new Error(detail || 'Please check your details and try again.');
      }
      setSent(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [form, params, slug]);

  if (loading) {
    return (
      <div className="bcard-shell">
        <Loader2 className="w-6 h-6 animate-spin" aria-label="Loading" />
      </div>
    );
  }

  if (notFound || !card) {
    return (
      <div className="bcard-shell">
        <div className="bcard">
          <h1 className="bcard-name">Card not found</h1>
          <p className="bcard-muted">This link may have expired or been replaced.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bcard-shell">
      <div className="bcard">
        {/* The logo is the tenant's configured primary mark. It is hidden
            rather than shown broken if the file is missing — a broken-image
            icon on a business card is worse than no logo. */}
        <img
          src={BRAND_ASSETS.logo.primary}
          alt={card.organization || 'Logo'}
          className="bcard-logo"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />

        <h1 className="bcard-name">{card.full_name}</h1>
        {card.job_title && <p className="bcard-title">{card.job_title}</p>}
        {card.organization && <p className="bcard-org">{card.organization}</p>}
        {card.headline && <p className="bcard-headline">{card.headline}</p>}

        <div className="bcard-actions">
          {card.phone && (
            <a className="bcard-action" href={`tel:${card.phone.replace(/[^\d+]/g, '')}`}>
              <Phone className="w-4 h-4" aria-hidden /> {card.phone}
            </a>
          )}
          {card.email && (
            <a className="bcard-action" href={`mailto:${card.email}`}>
              <Mail className="w-4 h-4" aria-hidden /> {card.email}
            </a>
          )}
          {card.website_url && (
            <a className="bcard-action" href={card.website_url} target="_blank" rel="noreferrer noopener">
              <Globe className="w-4 h-4" aria-hidden /> {card.website_url.replace(/^https?:\/\//, '')}
            </a>
          )}
          {card.linkedin_url && (
            <a className="bcard-action" href={card.linkedin_url} target="_blank" rel="noreferrer noopener">
              <Linkedin className="w-4 h-4" aria-hidden /> LinkedIn
            </a>
          )}
        </div>

        <a className="bcard-save" href={`${cardApi}/${slug}/vcard.vcf`}>
          <Download className="w-4 h-4" aria-hidden /> Save to contacts
        </a>

        <hr className="bcard-rule" />

        {sent ? (
          <div className="bcard-sent" role="status">
            <Check className="w-5 h-5" aria-hidden />
            <div>
              <strong>Thank you.</strong>
              <p className="bcard-muted">
                {card.full_name.split(' ')[0]} has your details and will be in touch.
              </p>
            </div>
          </div>
        ) : (
          <form className="bcard-form" onSubmit={submit}>
            <h2 className="bcard-form-title">Share your details</h2>

            <label className="bcard-label" htmlFor="bc-name">Name</label>
            <input
              id="bc-name" className="bcard-input" required autoComplete="name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <label className="bcard-label" htmlFor="bc-company">Company</label>
            <input
              id="bc-company" className="bcard-input" autoComplete="organization"
              value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
            />

            <label className="bcard-label" htmlFor="bc-email">Email</label>
            <input
              id="bc-email" className="bcard-input" type="email" required
              autoComplete="email" inputMode="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            />

            <label className="bcard-label" htmlFor="bc-phone">Phone</label>
            <input
              id="bc-phone" className="bcard-input" type="tel" autoComplete="tel" inputMode="tel"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />

            {formError && <p className="bcard-error" role="alert">{formError}</p>}

            <button className="bcard-submit" type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
              {submitting ? 'Sending' : 'Send'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
